import React from "react";
import { Paper, Box, Typography, Switch, Stack, Divider } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SmsIcon from "@mui/icons-material/Sms";
import {
  SitterEmailNotifications,
  SitterPushNotifications,
  SitterSmsNotifications,
} from "shared/types";

interface SitterNotificationsSectionProps {
  emailNotifications: SitterEmailNotifications;
  pushNotifications: SitterPushNotifications;
  smsNotifications: SitterSmsNotifications;
  onEmailChange: (key: keyof SitterEmailNotifications, value: boolean) => void;
  onPushChange: (key: keyof SitterPushNotifications, value: boolean) => void;
  onSmsChange: (key: keyof SitterSmsNotifications, value: boolean) => void;
}

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#178B8E" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#178B8E",
  },
};

interface SwitchRowProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}

const SwitchRow: React.FC<SwitchRowProps> = ({
  label,
  description,
  checked,
  onChange,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      py: 1.25,
    }}
  >
    <Box>
      <Typography variant="body2" fontWeight={500}>
        {label}
      </Typography>
      {description && (
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      )}
    </Box>
    <Switch
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      size="small"
      sx={switchSx}
    />
  </Box>
);

const GroupHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({
  icon,
  title,
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
    <Box sx={{ color: "#178B8E", display: "flex" }}>{icon}</Box>
    <Typography variant="body1" fontWeight={600}>
      {title}
    </Typography>
  </Box>
);

const SitterNotificationsSection: React.FC<SitterNotificationsSectionProps> = ({
  emailNotifications,
  pushNotifications,
  smsNotifications,
  onEmailChange,
  onPushChange,
  onSmsChange,
}) => {
  return (
    <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Typography variant="h6" fontWeight={600} mb={0.5}>
        Notifications
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Choose what you want to be notified about
      </Typography>

      {/* Email */}
      <GroupHeader
        icon={<EmailIcon fontSize="small" />}
        title="Email Notifications"
      />
      <Stack divider={<Divider />}>
        <SwitchRow
          label="New booking requests"
          checked={emailNotifications.bookingRequests}
          onChange={(v) => onEmailChange("bookingRequests", v)}
        />
        <SwitchRow
          label="Booking confirmations"
          checked={emailNotifications.confirmations}
          onChange={(v) => onEmailChange("confirmations", v)}
        />
        <SwitchRow
          label="Booking reminders"
          checked={emailNotifications.reminders}
          onChange={(v) => onEmailChange("reminders", v)}
        />
        <SwitchRow
          label="Messages from clients"
          checked={emailNotifications.messages}
          onChange={(v) => onEmailChange("messages", v)}
        />
        <SwitchRow
          label="Payment received"
          checked={emailNotifications.payments}
          onChange={(v) => onEmailChange("payments", v)}
        />
        <SwitchRow
          label="New reviews"
          checked={emailNotifications.reviews}
          onChange={(v) => onEmailChange("reviews", v)}
        />
        <SwitchRow
          label="Weekly earnings summary"
          description="Summary of bookings and earnings each week"
          checked={emailNotifications.weeklySummary}
          onChange={(v) => onEmailChange("weeklySummary", v)}
        />
      </Stack>

      <Divider sx={{ my: 2.5 }} />

      {/* Push */}
      <GroupHeader
        icon={<NotificationsIcon fontSize="small" />}
        title="Push Notifications"
      />
      <Stack divider={<Divider />}>
        <SwitchRow
          label="New booking requests"
          checked={pushNotifications.requests}
          onChange={(v) => onPushChange("requests", v)}
        />
        <SwitchRow
          label="New messages"
          checked={pushNotifications.messages}
          onChange={(v) => onPushChange("messages", v)}
        />
        <SwitchRow
          label="Booking updates"
          checked={pushNotifications.updates}
          onChange={(v) => onPushChange("updates", v)}
        />
      </Stack>

      <Divider sx={{ my: 2.5 }} />

      {/* SMS */}
      <GroupHeader
        icon={<SmsIcon fontSize="small" />}
        title="SMS Notifications"
      />
      <Stack divider={<Divider />}>
        <SwitchRow
          label="Critical updates only"
          description="Urgent booking changes and account alerts"
          checked={smsNotifications.critical}
          onChange={(v) => onSmsChange("critical", v)}
        />
      </Stack>
    </Paper>
  );
};

export default SitterNotificationsSection;
